import { useEffect, useState } from "react";
import clsx from "clsx";
import { heroApi } from "../api/hero";
import { stashApi } from "../api/stash";
import { useGameStore } from "../store/useGameStore";
import { RENAME_SCROLL_DIAMOND_COST, RENAME_SCROLL_ITEM_ID } from "../types";
import { ModalBackdrop } from "./ModalBackdrop";

interface RenameHeroModalProps {
  currentName: string;
  onClose: () => void;
  onRenamed: () => void;
}

export function RenameHeroModal({ currentName, onClose, onRenamed }: RenameHeroModalProps) {
  const { setScreen } = useGameStore();
  const [name, setName] = useState(currentName);
  const [scrollCount, setScrollCount] = useState(0);
  const [loadingStash, setLoadingStash] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    stashApi
      .getItems()
      .then((items) => {
        const scroll = items.find((item) => item.item_id === RENAME_SCROLL_ITEM_ID);
        setScrollCount(scroll?.quantity ?? 0);
      })
      .catch(() => setScrollCount(0))
      .finally(() => setLoadingStash(false));
  }, []);

  const trimmed = name.trim();
  const canSubmit =
    !loadingStash &&
    scrollCount > 0 &&
    trimmed.length >= 2 &&
    trimmed.length <= 20 &&
    trimmed !== currentName &&
    !saving;

  const handleRename = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await heroApi.renameHero(trimmed);
      onRenamed();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rename failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose} className="z-50 items-end bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white text-lg">Rename Hero</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">
            ✕
          </button>
        </div>

        <label className="block text-xs text-gray-400">
          New name
          <input
            type="text"
            value={name}
            maxLength={20}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
            placeholder="Enter hero name"
          />
        </label>

        <div className="text-xs text-gray-400 bg-kingdom-dark rounded-lg px-3 py-2">
          {loadingStash ? (
            "Checking inventory..."
          ) : scrollCount > 0 ? (
            <span className="text-emerald-300">Rename Scroll ×{scrollCount} available</span>
          ) : (
            <span className="text-amber-300">
              No Rename Scroll. Buy one in the Shop for 💎 {RENAME_SCROLL_DIAMOND_COST}.
            </span>
          )}
        </div>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded-lg p-2">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          {scrollCount === 0 && !loadingStash && (
            <button
              type="button"
              onClick={() => {
                onClose();
                setScreen("shop");
              }}
              className="flex-1 py-2.5 rounded-xl border border-gold-500/40 text-gold-300 text-sm font-bold"
            >
              Go to Shop
            </button>
          )}
          <button
            type="button"
            onClick={handleRename}
            disabled={!canSubmit}
            className={clsx(
              "flex-1 py-2.5 rounded-xl text-sm font-bold transition-all",
              canSubmit
                ? "bg-gradient-to-r from-kingdom-accent to-kingdom-glow text-white"
                : "bg-gray-800 text-gray-500 cursor-not-allowed",
            )}
          >
            {saving ? "Renaming..." : "Rename"}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
